import { useEffect, useMemo, useState } from "react"

import type { Conductor } from "types/conductors.types"
import type { Powerline } from "types/powerlines.types"

import { useLazyConductorQuery } from "features/api"
import { getConductorIds } from "helpers/utils/powerlines"

interface UsePowerlineConductorsProps {
  powerline: Powerline | undefined
}

interface UsePowerlineConductorsReturn {
  conductors: Conductor[]
  conductorIds: number[]
  isLoading: boolean
}

/**
 * The conductors a power line is built from. There is no endpoint for them as a
 * set, so each one is fetched by id and served from the cache after that.
 */
const usePowerlineConductors = ({
  powerline,
}: UsePowerlineConductorsProps): UsePowerlineConductorsReturn => {
  const [conductors, setConductors] = useState<Conductor[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [fetchConductor] = useLazyConductorQuery()

  const conductorIds = useMemo(
    () => (powerline ? getConductorIds(powerline) : []),
    [powerline],
  )

  useEffect(() => {
    if (!conductorIds.length) {
      setConductors([])
      return
    }

    let isMounted = true
    setIsLoading(true)

    const fetchAll = async () => {
      const settled = await Promise.allSettled(
        conductorIds.map((id) => fetchConductor({ id }, true).unwrap()),
      )
      if (!isMounted) return
      // Conductors are a shared catalog, so one the line points at may be gone.
      // Dropping it keeps the rest on screen.
      setConductors(
        settled.flatMap((result) =>
          result.status === "fulfilled" ? [result.value] : [],
        ),
      )
      setIsLoading(false)
    }
    void fetchAll()

    return () => {
      isMounted = false
    }
  }, [conductorIds, fetchConductor])

  return { conductors, conductorIds, isLoading }
}

export default usePowerlineConductors
